/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import cx from 'classnames';
import CustomRadioInput from '../FormGroup/RadioButton';
import { Label } from '@/features/i18n';
import { useLabels } from '@/hooks/useLabels';
import { countryCodes } from '@/constants/locale';
import { nddIcon } from '@/constants/images/nddIcon';
import { productInfoConstants } from '@/constants/productInfoConstants';
import { cartConstants } from '@/constants/cart';
import azCommonStyles from '@/theme/globals.module.scss';
import { convertToAmPm } from '@/utils/convertToAmPm';
import CountDown from '@/components/AZCustomComponent/CountDown';
import InfoToolTip from '@/components/AZCustomComponent/InfoToolTip';
import { Button } from '@/components/Button';
import { globalConstant } from '@/constants/global';
import { orderTypes } from '@/constants/orderTypes';
import NextImage from '@/components/NextImage';
import Availability from './Availability';
import styles from './styles.module.scss';
import { fulfillmentLabels } from '@/constants/fulfillmentConstants';
import { useGetAllHeaderData } from '@/features/header/api/useGetAllHeaderData';
import { infoIconWithGreenColor } from '@/constants/images';
import { type TSkuAvailability } from '@/features/product';
import { MyStoreModal } from '@/components/Store/StoreModal/MyStoreModal';
import { usePriceHiding } from '@/hooks/usePriceHiding';
import { Fragment, useState } from 'react';
import type { AvailabilityInfoVOFromAPI } from '@/types/availability';

type Props = {
  isVDPItem?: boolean;
  options: FulfillmentOption[];
  cartPage?: boolean;
  selectedValue: string;
  inline?: boolean;
  storeSelected?: boolean;
  onChange: (event: React.SyntheticEvent, activeValue: string) => void;
  applyNegativeMargin?: boolean;
  locale?: string | null;
  dollars?: string;
  cents?: string;
  availabilityInfoVO?: AvailabilityInfoVOFromAPI;
  isBopusView?: boolean;
  numberOfSisterStores?: number;
  skuAvailabilityInfo?: TSkuAvailability;
  phoneNumber?: string;
  hidePricing?: boolean;

  isPDP?: boolean;
};

type FulfillmentOption = {
  buttonIndex: string;
  disabled: boolean;
  isSelected: boolean;

  deliveryOptionLabel?: string;
  storeStockLabel?: React.ReactNode;

  sthoptions?: boolean;

  shippingAvailability?: {
    fulfillmentCutOffTime?: string;
    displayDate?: string;
    calculatedDate?: string;
    carrierHoliday?: boolean;
    holiday?: boolean;
    orderByMessage?: string | null;
  };

  isPriceDefined?: boolean;

  showFindNearStoreBtn?: boolean;

  storeAndAddToCartObj?: {
    label?: string;
    onClick: () => void;
  };

  storeDetails?: {
    address1: string;
  };
};

const labelMap = {
  Label_freeNextDayDeliveryEligible: 'Label_freeNextDayDeliveryEligible',
  label_cart_lineItem_WhatIsIt: 'label_cart_lineItem_WhatIsIt',
  label_Visit_Store_To_Purchase: 'label_Visit_Store_To_Purchase',
  label_MyAccountSTH_shipaddress_EstDelivery: 'label_MyAccountSTH_shipaddress_EstDelivery',
  labels_order_within: 'labels_order_within',
  labels_order_hrs: 'labels_order_hrs',
  labels_order_and: 'labels_order_and',
  labels_order_min: 'labels_order_min',
  labels_order_to: 'labels_order_to',
  labels_order_receive: 'labels_order_receive',
  labels_OrderCutOffTimeHead: 'labels_order_cut_off_time_head_ndd',
  labels_OrderCutOffTimeTail: 'labels_order_cut_off_time_tail_ndd',
  label_shelf_resultsList_price_not_available: 'label_shelf_resultsList_price_not_available',
  Label_InStock_SisterStores: 'Label_InStock_SisterStores',
  lblAtNearByStores: fulfillmentLabels.label_shipping_AtNearByStores,
  lblAtNearByStore: fulfillmentLabels.label_shipping_AtNearByStore,
  label_howDoes_it_works_mx: 'label_howDoes_it_works_mx',
  label_info_toolTip_body_mx: 'label_info_toolTip_body_mx',
  label_price_not_available_mx: 'label_price_not_available_mx',
  label_order_by_phone_mx: 'label_order_by_phone_mx',
  order_online_pickup_inStore_noCost_mx: 'order_online_pickup_inStore_noCost_mx',
  label_Footer_main_SendListToStore: 'label_Footer_main_SendListToStore',
  label_store_pickup: 'label_store_pickup',
  label_check_nearby_sister_store: 'label_check_nearby_sister_store',
};

type DisplayCustomPopperProps = {
  item: FulfillmentOption;
  props: Props;
  labels: typeof labelMap;
  openStoreModal: () => void;
};

const DisplayCustomPopper = ({ item, props, labels, openStoreModal }: DisplayCustomPopperProps) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZoneName: 'short',
  }).formatToParts(new Date());

  const timezone = parts.find((p) => p.type === 'timeZoneName')?.value;
  const freeNextDayDeliveryUi = (item: FulfillmentOption) => {
    const cutOffTimeISO = item.shippingAvailability?.fulfillmentCutOffTime;

    if (!cutOffTimeISO) {
      return null;
    }

    const cutOffTime = new Date(cutOffTimeISO).toTimeString();
    return (
      <div>
        <span
          className={cx(azCommonStyles['az-caption'], styles.freeNextDayDelivery)}
          id="freeNextDayDelivery"
        >
          <NextImage
            tabIndex={-1}
            role="presentation"
            src={nddIcon.src}
            alt={nddIcon.alt}
            width={33}
            height={16}
          />{' '}
          {labels.Label_freeNextDayDeliveryEligible}
        </span>
        <InfoToolTip
          classes={{
            icon: styles.nddInfo,
          }}
          header={labels.label_cart_lineItem_WhatIsIt}
          body={`${labels.labels_OrderCutOffTimeHead}
          ${convertToAmPm(cutOffTime, false)}
          ${timezone}${labels.labels_OrderCutOffTimeTail}
          `}
        />
        <CountDown
          shippingAvailability={item.shippingAvailability}
          storeStockLabel={item.storeStockLabel}
          labels={labels}
        />
      </div>
    );
  };

  const sisterStores = props.availabilityInfoVO?.numberOfSisterStoresAvailable
    ? props.availabilityInfoVO?.numberOfSisterStoresAvailable
    : props.numberOfSisterStores;

  let itemLabel = item.storeAndAddToCartObj?.label ?? '';
  if (
    sisterStores &&
    sisterStores >= 1 &&
    props.isBopusView &&
    (props.availabilityInfoVO?.sisterStoreAvailable || props.numberOfSisterStores)
  ) {
    const sisterStoreLabel = sisterStores > 1 ? labels.lblAtNearByStores : labels.lblAtNearByStore;

    itemLabel = sisterStoreLabel.replace(/(en|em)/, `$1 ${sisterStores}`);
  }
  const nextDayAvailable = item.sthoptions;
  const displDeliveryType = nextDayAvailable && freeNextDayDeliveryUi(item);
  const nextDayAvailableUi = (!nextDayAvailable || item.buttonIndex === globalConstant.store) &&
    !item.disabled &&
    !props.isBopusView && (
      <NextImage src="/images/green-tickmark.svg" alt="" width={14} height={11} />
    );
  const storeStockAvailable =
    !nextDayAvailable &&
    !(
      (props.availabilityInfoVO?.sisterStoreAvailable || item.showFindNearStoreBtn) &&
      props.isBopusView
    ) &&
    item.storeStockLabel;

  const renderInstockImageandLabel = () => (
    <>
      {!props.isBopusView && (
        <NextImage
          src={'/images/green-tickmark.svg'}
          alt={''}
          width={14}
          height={11}
          layout="fixed"
        />
      )}
      <span
        className={
          props.isBopusView ? styles.inStockSisterStoreLabelBopusMX : styles.inStockSisterStoreLabel
        }
        data-testid="sister-store-label"
      >
        {!props.isPDP ? labels.label_store_pickup : labels.Label_InStock_SisterStores}
      </span>
    </>
  );

  // MX SisterStoreLogicFlow 1
  const addToCartLink = (
    <>
      {renderInstockImageandLabel()}
      {props.isBopusView ? ' ' : ''}
      <Button
        variant="link"
        onClick={(e) => {
          const storeObj = item.storeAndAddToCartObj;

          if (!storeObj) {
            return;
          }

          storeObj.onClick();

          e.stopPropagation();
          e.preventDefault();
          e.nativeEvent.stopImmediatePropagation();
        }}
        customClass={props.isBopusView ? styles.normal : styles.bold}
        id="deliveryLink"
      >
        {!props.isPDP ? (
          labels.label_check_nearby_sister_store
        ) : props.isBopusView && sisterStores && sisterStores > 0 ? (
          itemLabel
        ) : (
          <Label label={itemLabel} />
        )}
      </Button>
    </>
  );

  const renderButton = (item: FulfillmentOption) => (
    <>
      {' | '}
      <Button
        variant="link"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          e.nativeEvent.stopImmediatePropagation();
          openStoreModal();
        }}
        customClass={props.isBopusView ? styles.storeAddressBopusMX : styles.storeAddressLink}
      >
        {props.isBopusView
          ? item?.storeDetails?.address1.toLowerCase()
          : item?.storeDetails?.address1}
      </Button>
    </>
  );
  const displayAddToCartUi =
    item.showFindNearStoreBtn && item.storeAndAddToCartObj && addToCartLink;
  const renderStoreNameLink = [
    globalConstant.store,
    orderTypes.storeOrder,
    productInfoConstants.bopus,
  ].includes(item.buttonIndex) &&
    !(
      (props.availabilityInfoVO?.sisterStoreAvailable || item.showFindNearStoreBtn) &&
      props.isBopusView
    ) && (
      <span className={styles.storeLink}>
        {item.disabled ? (
          <span
            className={cx(
              styles.notAvailableAtStore,
              props.isBopusView ? styles.notAvailableMX : styles.notAvailable,
            )}
          >
            {item?.storeDetails?.address1}
          </span>
        ) : (
          renderButton(item)
        )}
      </span>
    );
  const notAvailableInStockClass = item.disabled
    ? props.isBopusView
      ? styles.notAvailableMX
      : styles.notAvailable
    : styles.inStock;

  return (
    <Fragment>
      <>
        {displDeliveryType}
        <div className={notAvailableInStockClass}>
          {nextDayAvailableUi}{' '}
          <span data-testid="availability">
            {storeStockAvailable}
            {renderStoreNameLink}
          </span>
        </div>
        {displayAddToCartUi}{' '}
      </>
    </Fragment>
  );
};

type DisplayNoStoreSelectedProps = {
  item: FulfillmentOption;
};

const DisplayNoStoreSelected = ({ item }: DisplayNoStoreSelectedProps) => {
  const storeObj = item.storeAndAddToCartObj;

  if (!storeObj) {
    return null;
  }

  return (
    <Button
      variant="link"
      onClick={(e) => {
        storeObj.onClick();
        e.stopPropagation();
        e.preventDefault();
        e.nativeEvent.stopImmediatePropagation();
      }}
    >
      {storeObj.label ?? ''}
    </Button>
  );
};

const ToggleComponent = (props: Props) => {
  const labels = useLabels(labelMap);

  const [modalOpen, setModalOpen] = useState(false);

  const handleOnChangeCart = (event: React.SyntheticEvent, selectedValue: string) => {
    event.preventDefault();

    if (
      (selectedValue === globalConstant.store && !props.options[0].disabled) ||
      (selectedValue === globalConstant.online && !props.options[1].disabled)
    ) {
      props.onChange(event, selectedValue);
    }
  };

  const handleOnChange = (
    event: React.SyntheticEvent,
    selectedValue: string,
    itemDisabled: boolean,
  ) => {
    event.preventDefault();

    if (selectedValue && !itemDisabled) {
      props.onChange(event, selectedValue);
    }
  };

  const DisplayGiftCard = (
    <div className={styles.notAvailable} id="notAvailable">
      {labels.label_Visit_Store_To_Purchase}
    </div>
  );

  const subLabelOption = (item: FulfillmentOption) => {
    let nextDayAvailable = item.sthoptions;
    const { shippingAvailability } = item;
    const selectStore =
      !props.storeSelected &&
      (item.buttonIndex === orderTypes.storeOrder || item.buttonIndex === globalConstant.store) &&
      item.disabled;
    const dispAddtoCart = selectStore ? (
      <DisplayNoStoreSelected item={item} />
    ) : (
      <DisplayCustomPopper
        item={item}
        props={props}
        labels={labels}
        openStoreModal={openStoreModal}
      />
    );
    const displaySubLabel =
      item.storeStockLabel === cartConstants.giftCard ? DisplayGiftCard : dispAddtoCart;

    if (nextDayAvailable && shippingAvailability) {
      const currentDay = new Date(
        `${shippingAvailability.displayDate ?? ''} ${new Date().getFullYear()}`,
      );
      const estimatedDate = new Date(
        `${shippingAvailability.calculatedDate ?? ''} ${new Date().getFullYear()}`,
      );
      const daysDiffernce = (estimatedDate.getTime() - currentDay.getTime()) / (1000 * 3600 * 24);
      nextDayAvailable =
        !(shippingAvailability.carrierHoliday ?? false) &&
        !(shippingAvailability.holiday ?? false) &&
        shippingAvailability.orderByMessage != null &&
        daysDiffernce === 1;

      if (!nextDayAvailable && shippingAvailability.calculatedDate) {
        item.storeStockLabel = (
          <Fragment>
            {labels.label_MyAccountSTH_shipaddress_EstDelivery}
            {shippingAvailability.calculatedDate}
          </Fragment>
        );
      }
    }

    return (
      <div
        className={cx(
          azCommonStyles['az-caption'],
          azCommonStyles['az-align-left'],
          selectStore ? styles.selectStore : '',
        )}
      >
        {displaySubLabel}
      </div>
    );
  };

  const openStoreModal = () => {
    setModalOpen(true);
  };

  const closeStoreModal = () => {
    setModalOpen(false);
  };

  const storeModal = (modalOpen: boolean) => {
    if (!modalOpen) {
      return null;
    }

    return <MyStoreModal closeStoreModal={closeStoreModal} />;
  };

  const labelOption = (item: FulfillmentOption) => {
    if (props.locale === countryCodes.ptBr) {
      return null;
    }

    return (
      <div
        className={cx(
          azCommonStyles['az-caption'],
          azCommonStyles['az-align-left'],
          props.isBopusView ? styles.deliveryOptionLabelBopusMX : styles.deliveryOptionLabel,
        )}
        data-testid="delivery-option-label"
      >
        {item.deliveryOptionLabel === 'Ship To Home'
          ? 'Ship-To-Home'
          : item.deliveryOptionLabel}{' '}
      </div>
    );
  };

  const priceLabel = (item: FulfillmentOption) => {
    if (props.hidePricing) {
      return null;
    }

    return !item.isPriceDefined ? (
      <div
        className={cx(
          azCommonStyles['az-caption'],
          azCommonStyles['az-align-left'],
          styles.unavailablePriceLabel,
        )}
        id="price-availability-label"
        data-testid="price-availability-label"
      >
        {labels.label_shelf_resultsList_price_not_available}
      </div>
    ) : null;
  };

  const renderOption = (item: FulfillmentOption, radio: boolean) => {
    const { inline, cartPage = false, isBopusView } = props;
    const vdpComp = props.isBopusView ? (
      <div className={styles.vdpItemDisplay}>
        <NextImage src="/images/shape-store.svg" alt="shape store" width={22} height={19} />
        <div className={styles.vdpItemContainer}>
          <div
            className={cx(
              azCommonStyles['az-caption'],
              azCommonStyles['az-align-left'],
              styles.aditionalCost,
            )}
            id="price-availability-label"
            data-testid="price-availability-label"
          >
            {item.deliveryOptionLabel}
          </div>
          {props?.skuAvailabilityInfo &&
            Number(props?.skuAvailabilityInfo?.storeTotalPrice) <= 0 && (
              <div className={cx(azCommonStyles['az-align-left'], styles.vdpPriceNotAvailable)}>
                {labels.label_shelf_resultsList_price_not_available}
              </div>
            )}
          <div>
            <a
              className={cx(azCommonStyles['az-align-left'], styles.vdpPhoneLink)}
              href={`tel:+${props.phoneNumber}`}
            >
              {labels.label_order_by_phone_mx}
            </a>

            <span className={styles.infoToolTipWrapper}>
              <InfoToolTip
                classes={{ icon: styles.tooltipInfo }}
                placement="top"
                data-testid="mycart_rewards_tooltipicon"
                header={labels.label_howDoes_it_works_mx}
                body={labels.label_info_toolTip_body_mx}
                iconSrc={infoIconWithGreenColor}
              />
            </span>
          </div>
        </div>
      </div>
    ) : (
      <div className={styles.paddingForSTLS}>
        <div
          className={cx(
            azCommonStyles['az-caption'],
            azCommonStyles['az-align-left'],
            styles.aditionalCost,
          )}
        >
          {labels.label_Footer_main_SendListToStore}
        </div>
        <div>
          <a
            className={cx(azCommonStyles['az-align-left'], styles.vdpPhoneLink)}
            href={`tel:+${props.phoneNumber}`}
          >
            {labels.label_order_by_phone_mx}
          </a>

          <span className={styles.infoToolTipWrapper}>
            <InfoToolTip
              classes={{ icon: styles.tooltipInfo }}
              placement="top"
              data-testid="mycart_rewards_tooltipicon"
              header={labels.label_howDoes_it_works_mx}
              body={labels.label_info_toolTip_body_mx}
              iconSrc={infoIconWithGreenColor}
            />
          </span>
        </div>
      </div>
    );
    const option = radio ? (
      <CustomRadioInput
        data-testid={`optionButton${item.buttonIndex}`}
        name={`fullfilment-radio${item.buttonIndex}`}
        value={item.buttonIndex}
        disabled={item.disabled}
        checked={
          cartPage ? item.isSelected : !props.storeSelected || (!item.disabled && item.isSelected)
        }
        label={labelOption(item)}
        subLabel={subLabelOption(item)}
      />
    ) : isBopusView ? (
      <>
        <div>
          <NextImage src="/images/shape-store.svg" alt="shape store" width={22} height={19} />
        </div>
        <div>
          {labelOption(item)}
          {priceLabel(item)}
          {subLabelOption(item)}
        </div>
      </>
    ) : (
      <>
        <div>
          {labelOption(item)}
          {priceLabel(item)}
        </div>
        {subLabelOption(item)}
      </>
    );
    return (
      <div
        className={`${
          props.isVDPItem
            ? styles.vpdItemCard
            : inline && !isBopusView
              ? styles.inlineToggle
              : styles.segmentedToggle
        }`}
        key={item.buttonIndex}
        onClick={(event) => {
          if (radio && !item.isSelected) {
            if (cartPage) {
              handleOnChangeCart(event, item.buttonIndex);
            } else {
              handleOnChange(event, item.buttonIndex, item.disabled);
            }
          }
        }}
      >
        {props.isVDPItem ? vdpComp : option}
      </div>
    );
  };

  const { options = [], inline, isBopusView } = props;
  const optionsMapObj =
    props.locale === countryCodes.mx || props.locale === countryCodes.ptBr
      ? renderOption(options[0], false)
      : options?.map((item: FulfillmentOption) => renderOption(item, true));
  const toggleClass =
    inline && !isBopusView
      ? styles.inlineToggleContainer
      : isBopusView && (props.locale === countryCodes.mx || props.locale === countryCodes.ptBr)
        ? styles.segmentedBopusMXContainer
        : styles.segmentedToggleContainer;

  return (
    <Availability
      optionsMapObj={optionsMapObj}
      toggleClass={toggleClass}
      storeModal={storeModal}
      closeStoreModal={closeStoreModal}
      modalOpen={modalOpen}
      applyNegativeMargin={props.applyNegativeMargin}
    />
  );
};

const ToggleComponentComp = ({
  cartPage = false,
  inline = false,
  storeSelected = false,
  applyNegativeMargin = true,
  isBopusView = false,
  availabilityInfoVO,
  numberOfSisterStores,
  skuAvailabilityInfo,
  ...props
}: Props) => {
  const { storeData } = useGetAllHeaderData();
  const hidePricing = usePriceHiding();

  const phoneNumber = storeData?.phoneNumber;
  const fullfillmentOptions = skuAvailabilityInfo?.fulfillmentOptions?.[0];
  let isVDPItem = false;
  if (fullfillmentOptions && storeData) {
    isVDPItem =
      !fullfillmentOptions?.available &&
      !fullfillmentOptions?.sisterStoreAvailable &&
      fullfillmentOptions?.availableQty === -2;
  }
  return (
    <ToggleComponent
      cartPage={cartPage}
      inline={inline}
      storeSelected={storeSelected}
      isBopusView={isBopusView}
      applyNegativeMargin={applyNegativeMargin}
      availabilityInfoVO={availabilityInfoVO}
      numberOfSisterStores={numberOfSisterStores}
      phoneNumber={phoneNumber}
      skuAvailabilityInfo={skuAvailabilityInfo}
      hidePricing={hidePricing}
      {...props}
      isVDPItem={isVDPItem}
    />
  );
};

export default ToggleComponentComp;
